// https://docs.google.com/spreadsheets/d/1TbXtSm34mH44blZYy14YnpylD-kcEW_qVl_C3XjPHAM

import { uniq, without } from 'es-toolkit';

import { UserRole, UserRoleSet } from '@paragon/contract-library';

export function setRoleInRoleSet(roleSet: UserRoleSet, desiredRole: UserRole): UserRoleSet {
  const permissionsGrantedByRole = getPermissionsGrantedByRole(desiredRole);

  return {
    role: desiredRole,
    whitelist: without(roleSet.whitelist, ...permissionsGrantedByRole),
    blacklist: roleSet.blacklist,
  };
}

export function addPermissionToRoleSet(roleSet: UserRoleSet, permission: string): UserRoleSet {
  const grantedByRole = roleSet.role != null && getPermissionsGrantedByRole(roleSet.role).includes(permission);

  return {
    role: roleSet.role,
    whitelist: grantedByRole ? roleSet.whitelist : uniq(roleSet.whitelist.concat(permission)),
    blacklist: without(roleSet.blacklist, permission),
  };
}

export function removePermissionFromRoleSet(roleSet: UserRoleSet, permission: string): UserRoleSet {
  const grantedByRole = roleSet.role != null && getPermissionsGrantedByRole(roleSet.role).includes(permission);
  const wasWhitelisted = roleSet.whitelist.includes(permission);

  return {
    role: roleSet.role,
    whitelist: wasWhitelisted ? without(roleSet.whitelist, permission) : roleSet.whitelist,
    blacklist: grantedByRole ? uniq(roleSet.blacklist.concat(permission)) : roleSet.blacklist,
  };
}

/**
 * Note that this returns only the permissions directly granted by the given role. A user may have more or less
 *   permissions depending on their role set which includes a whitelist and blacklist.
 */
export function getPermissionsGrantedByRole(role: UserRole): string[] {
  return USER_ROLES[role] ?? [];
}

export function getPermissionsFromRoleSet({ role, whitelist, blacklist }: UserRoleSet): string[] {
  return without(uniq((role ? getPermissionsGrantedByRole(role) : []).concat(whitelist)), ...blacklist);
}

/**
 * The permissions that are not generally expected to be granted by a role, but should be available to be manually
 *   granted to any user on demand.
 */
export function getUnassociatedPermissions() {
  return uniq(MISC_PERMISSIONS.concat(FEATURE_FLAGS));
}

export function getAllKnownPermissions(): string[] {
  return uniq([...Object.values(USER_ROLES).flat(), ...getUnassociatedPermissions()]);
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// The entire rest of this file MUST remain in sync with the server-side `roles.js` in user-service.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Note that if you are renaming any permissions, you MUST also write a database migration to rename those permissions
//   in the whitelist and blacklist of any users that have them.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const STANDARD_CLIENT_SET = [
  'convert-access',
  'design-access',
  'markup-access',
  'materials-access',
  'packing-access',
  'pricing-access',
  'production-access',
  'projects-access',
  'review-access',
  'schedule-access',
];

export const USER_ROLES: { [role in UserRole]: string[] } = {
  Assembler: [
    // Client access
    'design-access',
    'production-access',

    // Core permissions
    'assembly',
  ],

  Free: [
    // Client access
    'design-access',
    'materials-access',
    'pricing-access',

    // Core permissions
    'truss-analysis',
    'truss-analysis-interact',
    'truss-design',
    'truss-design-downloads',
    'truss-properties-interact',
    'truss-settings',
    'subscription-manager',

    // Usage Tracking
    'free',
  ],

  ['Individual Estimator']: [
    // Client access
    'design-access',
    'materials-access',
    'pricing-access',

    // Core permissions
    'can-edit-materials',
    'can-edit-prices',
    'edit-bearing-conditions',
    'new-layout-mode',
    'truss-analysis',
    'truss-analysis-interact',
    'truss-design',
    'truss-design-downloads',
    'truss-properties-interact',
    'truss-settings',
    'paragon-branded-share-emails',
    'api-sessions',
    'subscription-manager',
    'project-hierarchy',

    // Feature flags

    // Usage Tracking
    'standard',
  ],

  ['Individual Designer']: [
    // Client access
    'design-access',
    'materials-access',
    'pricing-access',

    // Core permissions
    'can-edit-materials',
    'can-edit-prices',
    'edit-bearing-conditions',
    'new-layout-mode',
    'truss-analysis',
    'truss-analysis-interact',
    'truss-design',
    'truss-design-downloads',
    'truss-properties-interact',
    'truss-settings',
    'paragon-branded-share-emails',
    'api-sessions',
    'can-use-plates',
    'subscription-manager',
    'engineer-network-access',
    'project-hierarchy',

    // Feature flags

    // Usage Tracking
    'standard',
  ],

  ['Estimate Team']: [
    // Client access
    'design-access',
    'materials-access',
    'pricing-access',
    'production-access',
    'projects-access',
    'review-access',
    'schedule-access',

    // Core permissions
    'can-edit-materials',
    'can-edit-prices',
    'edit-bearing-conditions',
    'new-layout-mode',
    'truss-analysis',
    'truss-analysis-interact',
    'truss-design',
    'truss-design-downloads',
    'truss-properties-interact',
    'truss-settings',
    'paragon-branded-share-emails',
    'api-sessions',
    'project-hierarchy',

    // Usage Tracking
    'team',
  ],

  ['Design Team']: [
    // Client access
    'design-access',
    'materials-access',
    'pricing-access',
    'production-access',
    'projects-access',
    'review-access',
    'schedule-access',

    // Core permissions
    'can-edit-materials',
    'can-edit-prices',
    'edit-bearing-conditions',
    'new-layout-mode',
    'truss-analysis',
    'truss-analysis-interact',
    'truss-design',
    'truss-design-downloads',
    'truss-properties-interact',
    'truss-settings',
    'paragon-branded-share-emails',
    'api-sessions',
    'can-use-plates',
    'engineer-network-access',
    'production-group-management',
    'project-hierarchy',

    // Usage Tracking
    'team',
  ],

  ['Enterprise Team']: [
    // Client access
    'design-access',
    'materials-access',
    'pricing-access',
    'production-access',
    'projects-access',
    'review-access',
    'schedule-access',

    // Core permissions
    'can-edit-materials',
    'can-edit-prices',
    'edit-bearing-conditions',
    'new-layout-mode',
    'seals-signatures-markup',
    'truss-analysis',
    'truss-analysis-interact',
    'truss-design',
    'truss-design-downloads',
    'truss-properties-interact',
    'truss-settings',
    'paragon-branded-share-emails',
    'api-sessions',
    'can-use-plates',
    'engineer-network-access',
    'project-hierarchy',

    // Feature flags
    'optimization',

    // Usage Tracking
    //   None for now; approved by Scotty. Usage is billed manually per each contract.
  ],

  Root: [
    // Client access
    'accounts-access',
    ...STANDARD_CLIENT_SET,

    // Core permissions
    'reviewer',
    'new-layout-mode',
    'engineer',
    'root',
    'truss-settings',
    'truss-analysis',
    'truss-analysis-interact',
    'truss-design',
    'truss-design-downloads',
    'truss-properties-interact',
    'analysis-evaluation',
    'can-edit-materials',
    'can-edit-prices',
    'paragon-branded-share-emails',
    'api-sessions',
    'can-use-plates',
    'subscription-manager',
    'engineer-network-access',
    'project-hierarchy',

    // Feature flags
    'can-edit-design-values',
    'create-load-cases',
    'edit-bearing-conditions',
    'hanger-upgrade-and-analyze',
    'optimization',
    'production-supervisor',
    'production-group-management',

    'seals-signatures-markup',
    'compare-plate-prices',
  ],
};

// These permissions are not granted by any role, but can be granted individually. Use them whenever you need to control
//   the visibility of features or access to API routes, but don't want to tie them to a role, or want to be able to
//   edit them on a per-user basis. They are listed here so that they will show up in the role editor.
export const MISC_PERMISSIONS = [
  'truss-conversion',
  'multi-select',
  'production-supervisor',
  'production-group-management',
  'deactivated',
  'can-edit-design-values',
  'organization-templates',

  // Marks a user as needing to be notified (via email) when a job is shared with their organization
  'organization-job-contact',

  // Restricts a user to only using CH Machine plates
  'ch-machine-only-user',
];

// Feature flags are listed here for the same reasons as the misc. permissions above, but they have a subtly different
//   definition and behavior. Feature flags are (ideally) temporary, and can sometimes be granted by roles. Normally we
//   use feature flags for features that are in development, or need to be temporarily hidden from some users.
export const FEATURE_FLAGS = [
  'sync-component',
  'optimization',
  'layout-optimization',
  'new-layout-mode',
  'eager-envelope-recalculation',
  'auto-cut-on-overlap',
  'hanger-upgrade-and-analyze',
  'blade-downloads',
  'cyb-downloads',
  'trs-downloads',
  'edit-end-conditions',
  'create-load-cases',
  'tre-preserve-plate-type',
  'compare-plate-prices',
  'pdf-segment-upload-ai-scale',
  'auto-layout',
  'webhooks',
  'crowdbuild-stitcher-import',
  'steel-trs-profile',
  'assistant',
];
